import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { DataService } from "src/app/core/core-services/services/dataservices/data.service";

export interface ImportedFlyCut {
  shortcut: string;
  title?: string;
  html: string;
  text: string;
  uploads: unknown[];
  category_id?: string;
  category?: string;
}

export interface ImportFailure {
  /** Submitted-row indexes reported by the API's structured error_list. */
  conflictIndexes: number[];
  errors: Record<string, string[]>;
  network: boolean;
  planLimited: boolean;
}

export type ImportResult = { kind: "success" } | { kind: "failure"; failure: ImportFailure };

export interface ReplacementOutcome {
  index: number;
  succeeded: boolean;
  errors: string[];
}

export type ReplacementResult =
  | { kind: "outcomes"; outcomes: ReplacementOutcome[] }
  | { kind: "failure"; failure: ImportFailure };

/**
 * Import endpoints deliberately opt out of DataService's generic error UI.
 * Indexed validation errors are recovery state for SettingsComponent, not a
 * global toast: it uses the submitted snapshot to return users to the exact
 * rows that need attention.
 */
@Injectable({ providedIn: "root" })
export class ImportShortcutApiService {
  constructor(private readonly dataService: DataService) {}

  import(rows: ImportedFlyCut[]): Observable<ImportResult> {
    return this.dataService.post({ url: "v1/user/import/shortcut", data: rows, ignoreErrors: true }).pipe(
      map((response: unknown) => this.normaliseImportResponse(response)),
      catchError((error: unknown) => of<ImportResult>({ kind: "failure", failure: this.normaliseError(error) }))
    );
  }

  replace(rows: ImportedFlyCut[]): Observable<ReplacementResult> {
    return this.dataService
      .post({ url: "v1/user/replace/shortcut", data: { shortcuts: rows }, ignoreErrors: true })
      .pipe(
        map((response: unknown) => this.normaliseReplacementResponse(response, rows.length)),
        catchError((error: unknown) => of<ReplacementResult>({ kind: "failure", failure: this.normaliseError(error) }))
      );
  }

  private normaliseImportResponse(response: any): ImportResult {
    if (response?.result === true) return { kind: "success" };

    return { kind: "failure", failure: this.normaliseError(response) };
  }

  private normaliseReplacementResponse(response: any, length: number): ReplacementResult {
    const outcomes = Array.isArray(response?.result) ? response.result : Array.isArray(response) ? response : null;
    const indexes = outcomes?.map((outcome) => outcome?.index);
    if (
      indexes?.length === length &&
      indexes.every((index) => Number.isInteger(index) && index >= 0 && index < length) &&
      new Set(indexes).size === length
    ) {
      return {
        kind: "outcomes",
        outcomes: outcomes.map((outcome) => this.normaliseReplacementOutcome(outcome)),
      };
    }

    return { kind: "failure", failure: this.normaliseError(response) };
  }

  private normaliseError(response: any): ImportFailure {
    const body = response?.error_list ? response : response?.result?.error_list ? response.result : response?.error ?? response ?? {};
    const errors = body?.error_list && typeof body.error_list === "object" ? body.error_list : {};
    const conflictIndexes = Object.keys(errors)
      .map((key) => this.indexFromErrorKey(key))
      .filter((index): index is number => index !== null);

    return {
      conflictIndexes: [...new Set(conflictIndexes)],
      errors,
      network: !body?.error_list,
      planLimited: response?.status === 403,
    };
  }

  private normaliseReplacementOutcome(outcome: any): ReplacementOutcome {
    const errorList = outcome?.error_list ?? outcome?.errors;
    const listedErrors = errorList && typeof errorList === "object" ? Object.values(errorList).flat() : [];
    const directError = typeof outcome?.error === "string" ? [outcome.error] : [];
    const directMessage = typeof outcome?.message === "string" ? [outcome.message] : [];
    const errors = [...listedErrors, ...directError, ...directMessage].filter((error): error is string => typeof error === "string");
    const succeeded = Boolean(outcome?.shortcut_id) && outcome?.status !== "failed" && !errors.length;

    return { index: outcome.index, succeeded, errors };
  }

  /** Both endpoints key code conflicts by submitted row index. */
  private indexFromErrorKey(key: string): number | null {
    const match = /^(?:shortcuts\.)?(\d+)\.shortcut$/.exec(key);
    return match ? Number(match[1]) : null;
  }
}
